import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ElementRef,
  HostListener,
  AfterViewInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  DataService,
  LogService,
  FaqModalService,
  Question,
  Section,
  decodeHtml,
  initializeFaqCodeSnippetCopyKeys
} from '@adscore/adscore-frontend-common';
import { PrismService } from 'services/prism.service';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CommonModule, Location } from '@angular/common';
import { ClipboardService } from 'ngx-clipboard';
import { takeUntil } from 'rxjs/operators';
import { AdscoreMinModule } from 'adscore/adscore-min.module';
import { SectionTileComponent } from './section-tile/section-tile.component';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

@Component({
  standalone: true,
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.scss'],
  imports: [AdscoreMinModule, SectionTileComponent, CommonModule, FontAwesomeModule],
  providers: [FaqModalService]
})
export class FaqComponent implements OnInit, OnDestroy, AfterViewInit {
  sections: Section[] = [];
  openSection: Section | null;
  openQuestion: Question | null;
  adscoreContactData$: Subscription;
  gotContactData = false;
  // routing
  sectionToOpen: string;

  scrollTopToRestore = 0;

  faArrowLeft = faArrowLeft;

  private destroy$ = new Subject<boolean>();

  @ViewChildren('map') private mapElement: QueryList<ElementRef<HTMLIFrameElement>>;
  @ViewChildren('answerContent') private answerContent: QueryList<ElementRef<HTMLParagraphElement>>;

  @HostListener('document:visibilitychange')
  onVisibilitychange() {
    if (!globalThis.document?.hidden) {
      this.loadMapIfNeeded();
    }
  }

  @HostListener('window:popstate')
  onPopState(): void {
    window.setTimeout(() => {
      if (this.route.snapshot.params.sectionSlug && location.pathname !== '/faq') {
        this.handleNavigation();
      } else {
        // Restore scroll when going back from answer with browser back button
        window.scrollTo(0, this.scrollTopToRestore);
      }
    }, 0);
  }

  constructor(
    private dataService: DataService,
    public prismService: PrismService,
    private router: Router,
    private route: ActivatedRoute,
    private faqModalService: FaqModalService,
    private title: Title,
    private location: Location,
    private clipboardService: ClipboardService,
    private logService: LogService
  ) {}

  ngOnInit() {
    this.handleNavigation();

    this.adscoreContactData$ = this.dataService
      .updatables('adscoreContactData')
      .observable.pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.gotContactData = data && data.email !== '';
      });
  }

  ngAfterViewInit() {
    if (!globalThis.document?.hidden) {
      this.loadMapIfNeeded();
    }
  }

  handleNavigation() {
    this.openSection = null;
    this.openQuestion = null;
    this.sectionToOpen = this.route.snapshot.params.sectionSlug;
    this.sections = this.faqModalService.getSections();
    if (this.sectionToOpen) {
      const sectionToShow = this.sections.find((section) => section.id === this.sectionToOpen);
      if (sectionToShow) {
        this.showSection(sectionToShow);
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  showAnswer(q: Question, section: Section | null = null): boolean {
    this.router.navigate(['faq', q.slug]);
    this.title.setTitle(decodeHtml(q.text) + ' | Adscore');
    this.openQuestion = q;
    window.setTimeout(() => {
      initializeFaqCodeSnippetCopyKeys(this.answerContent, this.clipboardService, this.logService);
    }, 0);
    if (section) {
      this.openSection = section;
    }
    this.scrollTopToRestore = window.scrollY;
    return false;
  }

  showSection(section: Section, scrollToTop = false) {
    this.router.navigate(['faq/section', section.id]);
    if (scrollToTop) {
      window.scroll(0, 0);
    }
  }

  showFaq() {
    this.openSection = null;
    this.openQuestion = null;
    this.router.navigate(['faq']);
  }

  loadMapIfNeeded() {
    if (this.mapElement?.last && !this.mapElement.last.nativeElement.src) {
      this.mapElement.last.nativeElement.src =
        'https://www.openstreetmap.org/export/embed.html?bbox=55.14573991298676%2C25.075111545251165%2C55.15335738658906%2C25.07819929762256&amp;layer=mapnik';
    }
  }

  goBack(): void {
    this.location.back();
  }
}
